import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DutyRosterService } from "@/services/dutyRosterService";
import { HolidayOverrideService } from "@/services/holidayOverrideService";
import { supabase } from "@/integrations/supabase/client";
import { 
  DutyRosterGroup
} from "@/types";
import { toast } from "sonner";

/**
 * Hook for managing duty roster groups
 */
export function useDutyRosterManagement(companyId: string) {
  const queryClient = useQueryClient();

  // Fetch groups
  const {
    data: groups,
    isLoading: groupsLoading,
    error: groupsError
  } = useQuery({
    queryKey: ['dutyRosterGroups', companyId],
    queryFn: () => DutyRosterService.fetchGroups(companyId),
    enabled: !!companyId
  });

  // Create group mutation
  const createGroupMutation = useMutation({
    mutationFn: (groupData: Partial<DutyRosterGroup>) => 
      DutyRosterService.createGroup(groupData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dutyRosterGroups', companyId] });
      toast.success('Duty roster group created successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create group: ${error.message}`);
    }
  });

  // Update group mutation
  const updateGroupMutation = useMutation({
    mutationFn: ({ groupId, updates }: { groupId: string; updates: Partial<DutyRosterGroup> }) =>
      DutyRosterService.updateGroup(groupId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dutyRosterGroups', companyId] });
      toast.success('Group updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update group: ${error.message}`);
    }
  });

  // Delete group mutation
  const deleteGroupMutation = useMutation({
    mutationFn: (groupId: string) => DutyRosterService.deleteGroup(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dutyRosterGroups', companyId] });
      toast.success('Group deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete group: ${error.message}`);
    }
  });

  return {
    groups: groups || [],
    groupsLoading,
    groupsError,
    createGroup: createGroupMutation.mutate,
    updateGroup: updateGroupMutation.mutate,
    deleteGroup: deleteGroupMutation.mutate,
    isCreating: createGroupMutation.isPending,
    isUpdating: updateGroupMutation.isPending,
    isDeleting: deleteGroupMutation.isPending
  };
}

/**
 * Hook for managing employee duty roster assignments
 */
export function useEmployeeDutyRoster(employeeId: string) {
  const queryClient = useQueryClient();

  // Fetch employee's group information
  const {
    data: employeeGroup,
    isLoading: groupLoading,
    error: groupError
  } = useQuery({
    queryKey: ['employeeDutyRoster', employeeId],
    queryFn: async () => {
      // First get the employee's group_id, then fetch group details
      const { data: employee, error } = await supabase
        .from('employees')
        .select('group_id, is_duty_roster')
        .eq('id', employeeId)
        .single();
      
      if (error) throw error;
      return employee;
    },
    enabled: !!employeeId
  });

  // Assign employee to group mutation with validation
  const assignToGroupMutation = useMutation({
    mutationFn: ({ groupId, assignedBy }: { groupId: string; assignedBy?: string }) => 
      DutyRosterService.assignEmployeeToGroupWithValidation(employeeId, groupId, assignedBy),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['employeeDutyRoster', employeeId] });
      queryClient.invalidateQueries({ queryKey: ['employeesByDutyRosterStatus'] });
      queryClient.invalidateQueries({ queryKey: ['dutyRosterEmployeesWithGroups'] });
      toast.success(data.message || 'Employee assigned to group successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to assign employee: ${error.message}`);
    }
  });

  // Remove employee from group mutation with validation
  const removeFromGroupMutation = useMutation({
    mutationFn: (removedBy?: string) => 
      DutyRosterService.removeEmployeeFromGroupWithValidation(employeeId, removedBy),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['employeeDutyRoster', employeeId] });
      queryClient.invalidateQueries({ queryKey: ['employeesByDutyRosterStatus'] });
      queryClient.invalidateQueries({ queryKey: ['dutyRosterEmployeesWithGroups'] });
      toast.success(data.message || 'Employee removed from group successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to remove employee: ${error.message}`);
    }
  });

  return {
    employeeGroup,
    groupLoading,
    groupError,
    assignToGroup: assignToGroupMutation.mutate,
    removeFromGroup: removeFromGroupMutation.mutate,
    isAssigning: assignToGroupMutation.isPending,
    isRemoving: removeFromGroupMutation.isPending
  };
}

/**
 * Hook for managing group schedules and off days
 */
export function useGroupSchedule(groupId: string, monthYear: string) {
  const queryClient = useQueryClient();

  // Fetch group off days
  const {
    data: offDays,
    isLoading: offDaysLoading,
    error: offDaysError
  } = useQuery({
    queryKey: ['groupOffDays', groupId, monthYear],
    queryFn: () => DutyRosterService.fetchGroupOffDays(groupId, monthYear),
    enabled: !!groupId && !!monthYear
  });

  // Generate roster mutation
  const generateRosterMutation = useMutation({
    mutationFn: ({ year, month, shiftId }: { year: number; month: number; shiftId: string }) =>
      DutyRosterService.generateMonthlyRoster(groupId, year, month, shiftId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groupOffDays', groupId, monthYear] });
      toast.success('Monthly roster generated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to generate roster: ${error.message}`);
    }
  });

  return {
    offDays,
    offDaysLoading,
    offDaysError,
    generateRoster: generateRosterMutation.mutate,
    isGenerating: generateRosterMutation.isPending
  };
}

/**
 * Hook for managing holiday overrides
 */
export function useHolidayOverrides(groupId: string, monthYear?: string) {
  const queryClient = useQueryClient();

  // Fetch holiday overrides for all groups in current year
  const {
    data: overrides,
    isLoading: overridesLoading,
    error: overridesError
  } = useQuery({
    queryKey: ['holidayOverrides', 'all-groups', 'current-year'],
    queryFn: () => HolidayOverrideService.fetchHolidayOverrides(groupId, monthYear),
    enabled: true // Always enabled since we fetch all groups
  });

  // Manual collision detection mutation
  const detectCollisionsMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('detect_all_holiday_collisions_manual');
      
      if (error) {
        throw new Error(`Failed to detect collisions: ${error.message}`);
      }
      
      if (!data.success) {
        throw new Error(data.message || 'Collision detection failed');
      }
      
      return data;
    },
    onSuccess: (data) => {
      // Invalidate holiday override queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['holidayOverrides', 'all-groups', 'current-year'] });
      
      // Use new statistics format from enhanced function
      const stats = data.statistics || {};
      const total = stats.total_collisions || 0;
      const newCount = stats.new_overrides || 0;
      const updated = stats.updated_overrides || 0;
      const obsolete = stats.obsolete_removed || 0;
      const invalid = stats.invalid_removed || 0;
      
      // Build dynamic message based on what actually happened
      const parts = [];
      if (newCount > 0) parts.push(`${newCount} new`);
      if (updated > 0) parts.push(`${updated} updated`);
      if (obsolete > 0) parts.push(`${obsolete} obsolete`);
      if (invalid > 0) parts.push(`${invalid} invalid`);
      
      const details = parts.length > 0 ? ` (${parts.join(', ')})` : '';
      
      toast.success(
        `Collision detection complete: ${total} active collisions${details}`
      );
    },
    onError: (error: Error) => {
      toast.error(`Failed to detect collisions: ${error.message}`);
    }
  });

  // Delete override mutation
  const deleteOverrideMutation = useMutation({
    mutationFn: (overrideId: string) => HolidayOverrideService.deleteHolidayOverride(overrideId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['holidayOverrides', 'all-groups', 'current-year'] });
      toast.success('Holiday override deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete override: ${error.message}`);
    }
  });

  return {
    overrides: overrides || [],
    overridesLoading,
    overridesError,
    detectCollisions: detectCollisionsMutation.mutate,
    isDetecting: detectCollisionsMutation.isPending,
    deleteOverride: deleteOverrideMutation.mutate,
    isDeleting: deleteOverrideMutation.isPending
  };
}

/**
 * Hook for validating overtime requests against duty roster
 */
export function useDutyRosterOvertimeValidation() {
  const validateOvertimeMutation = useMutation({
    mutationFn: ({ employeeId, date, shiftId }: { employeeId: string; date: string; shiftId: string }) =>
      DutyRosterService.validateOvertimeRequest(employeeId, date, shiftId),
    onError: (error: Error) => {
      toast.error(`Validation failed: ${error.message}`);
    }
  });

  return {
    validateOvertime: validateOvertimeMutation.mutate,
    validationResult: validateOvertimeMutation.data,
    isValidating: validateOvertimeMutation.isPending,
    validationError: validateOvertimeMutation.error
  };
}

// NEW: Employee Management Hooks

/**
 * Hook for bulk assigning employees to a group
 */
export function useBulkEmployeeGroupAssignment(companyId: string) {
  const queryClient = useQueryClient();

  const bulkAssignMutation = useMutation({
    mutationFn: ({ employeeIds, groupId, assignedBy }: { 
      employeeIds: string[]; 
      groupId: string; 
      assignedBy?: string 
    }) => 
      DutyRosterService.bulkAssignEmployeesToGroupWithValidation(employeeIds, groupId, assignedBy),
    onSuccess: (data) => {
      // Invalidate all relevant queries
      queryClient.invalidateQueries({ queryKey: ['employeesByDutyRosterStatus', companyId] });
      queryClient.invalidateQueries({ queryKey: ['dutyRosterEmployeesWithGroups', companyId] });
      queryClient.invalidateQueries({ queryKey: ['ungroupedDutyRosterEmployees', companyId] });
      
      // Show detailed success message
      if (data.successfulAssignments > 0) {
        toast.success(`Successfully assigned ${data.successfulAssignments} employees to group`);
      }
      
      if (data.failedAssignments > 0) {
        toast.error(`Failed to assign ${data.failedAssignments} employees. Check console for details.`);
        console.error('Failed assignments:', data.failedDetails);
      }
    },
    onError: (error: Error) => {
      toast.error(`Bulk assignment failed: ${error.message}`);
    }
  });

  return {
    bulkAssign: bulkAssignMutation.mutate,
    isBulkAssigning: bulkAssignMutation.isPending,
    bulkAssignResult: bulkAssignMutation.data,
    bulkAssignError: bulkAssignMutation.error
  };
}

/**
 * Hook for managing employee duty roster status
 */
export function useEmployeeDutyRosterStatus(companyId: string) {
  const queryClient = useQueryClient();

  // Fetch employees by duty roster status
  const {
    data: employeesData,
    isLoading: employeesLoading,
    error: employeesError
  } = useQuery({
    queryKey: ['employeesByDutyRosterStatus', companyId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_employees_by_duty_roster_status', {
        p_company_id: companyId,
        p_is_duty_roster: null, // Get all employees
        p_include_inactive: false
      });
      
      if (error) throw error;
      return data;
    },
    enabled: !!companyId
  });

  // Fetch duty roster employees with groups
  const {
    data: dutyRosterData,
    isLoading: dutyRosterLoading,
    error: dutyRosterError
  } = useQuery({
    queryKey: ['dutyRosterEmployeesWithGroups', companyId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_duty_roster_employees_with_groups', {
        p_company_id: companyId,
        p_group_id: null
      });
      
      if (error) throw error;
      return data;
    },
    enabled: !!companyId
  });

  return {
    employeesData,
    employeesLoading,
    employeesError,
    dutyRosterData,
    dutyRosterLoading,
    dutyRosterError,
    refreshData: () => {
      queryClient.invalidateQueries({ queryKey: ['employeesByDutyRosterStatus', companyId] });
      queryClient.invalidateQueries({ queryKey: ['dutyRosterEmployeesWithGroups', companyId] });
    }
  };
}

/**
 * Hook for validating employee-group residency compatibility
 */
export function useEmployeeGroupResidencyValidation() {
  const validateResidencyMutation = useMutation({
    mutationFn: ({ employeeId, groupId }: { employeeId: string; groupId: string }) =>
      DutyRosterService.validateEmployeeGroupResidency(employeeId, groupId),
    onError: (error: Error) => {
      toast.error(`Residency validation failed: ${error.message}`);
    }
  });

  return {
    validateResidency: validateResidencyMutation.mutate,
    validationResult: validateResidencyMutation.data,
    isValidating: validateResidencyMutation.isPending,
    validationError: validateResidencyMutation.error
  };
}

/**
 * Hook for validating employee duty roster assignment
 */
export function useEmployeeDutyRosterAssignmentValidation() {
  const validateAssignmentMutation = useMutation({
    mutationFn: (employeeId: string) =>
      DutyRosterService.validateEmployeeDutyRosterAssignment(employeeId),
    onError: (error: Error) => {
      toast.error(`Assignment validation failed: ${error.message}`);
    }
  });

  return {
    validateAssignment: validateAssignmentMutation.mutate,
    validationResult: validateAssignmentMutation.data,
    isValidating: validateAssignmentMutation.isPending,
    validationError: validateAssignmentMutation.error
  };
}
